import { cn } from '@my/ui';
import { Box, Truck, Users, type LucideIcon } from 'lucide-react';
import { ContactsImportTab } from '@/features/parties/import/ContactsImportTab';
import { OrdersImportTab } from '@/features/settings/import/orders/OrdersImportTab';
import { ResourcesImportTab } from '@/features/settings/import/resources/ResourcesImportTab';
import { IMPORT_TABS, useImportViewStore, type ImportTab } from '@/stores/importView';

const TAB_LABELS: Record<ImportTab, string> = {
  Contacts: 'Contacts',
  Orders: 'Orders',
  Resources: 'Resources',
};

/** Mirrors the sidebar glyphs (Users = Contacts, Box = Orders, Truck =
 *  Resources). Sized to match the Work View tab icons (34×34). */
const TAB_ICONS: Record<ImportTab, LucideIcon> = {
  Contacts: Users,
  Orders: Box,
  Resources: Truck,
};

const TAB_DESCRIPTIONS: Record<ImportTab, string> = {
  Contacts: 'Bulk-create customers or suppliers from a JSON, CSV or Excel file.',
  Orders: 'Bulk-create orders, their tasks and cargo from a file.',
  Resources: 'Bulk-create drivers, trucks, rigids or trailers from a JSON, CSV or Excel file.',
};

/**
 * Settings → Import. Hosts the bulk-import tools as tabs:
 *   - Contacts  — the party import wizard.
 *   - Orders    — header + tasks + cargo importer.
 *   - Resources — drivers / trucks / rigids / trailers (type picked first).
 *
 * The active tab is persisted across refreshes via `useImportViewStore`
 * (mirrors the Work View tab pattern — single route, tab in localStorage).
 * Owner / Admin only (gated at the route, shares the Settings guard).
 */
export function ImportPage() {
  const currentTab = useImportViewStore((s) => s.currentTab);
  const setCurrentTab = useImportViewStore((s) => s.setCurrentTab);

  return (
    <div className="flex h-full flex-col px-2">
      {/* Tabs first (like the Work View top bar), then the title + steps below. */}
      <ImportTabs current={currentTab} onChange={setCurrentTab} />

      {/* Inline header — title + smaller italic description on one line, matching
          the Manifesting top-bar title style. */}
      <div className="flex flex-wrap items-baseline gap-x-2 px-1 py-2">
        <h1 className="text-sm font-semibold text-slate-700">Import</h1>
        <span className="text-xs italic text-slate-400">{TAB_DESCRIPTIONS[currentTab]}</span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {currentTab === 'Contacts' && <ContactsImportTab />}
        {currentTab === 'Orders' && <OrdersImportTab />}
        {currentTab === 'Resources' && <ResourcesImportTab />}
      </div>
    </div>
  );
}

function ImportTabs({
  current,
  onChange,
}: {
  current: ImportTab;
  onChange: (tab: ImportTab) => void;
}) {
  return (
    <div className="mb-1.5 flex w-full justify-center rounded-md p-1">
      <div className="rounded-md bg-slate-100 p-1 shadow-sm">
        {IMPORT_TABS.map((tab) => {
          const isActive = tab === current;
          const Icon = TAB_ICONS[tab];
          return (
            <button
              key={tab}
              type="button"
              onClick={() => onChange(tab)}
              aria-label={TAB_LABELS[tab]}
              title={TAB_LABELS[tab]}
              className={cn(
                'inline-flex h-10 w-14 items-center justify-center rounded-md p-2 transition-colors',
                isActive
                  ? 'bg-white text-slate-600 shadow-sm'
                  : 'text-slate-400 hover:text-slate-900',
              )}
            >
              <Icon size={34} strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
